<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import { push, replace, location } from 'svelte-spa-router';
	import { format } from 'svelte-i18n';
	import { Container, Button, Modal, Text, Stack } from '@Shelly/shelly-design-system';
	import type { ISceneObj, SceneExecutionHistoryEntry } from '../../types/SceneType';
	import { scenes } from 'src/stores/lists/scenes';
	import { flowEditorState, sceneMetadata, flushLiveFlowPositions } from '../../stores/flow_editor';
	import { graphToScene, mergeIfWithIndailyintIntervals } from './converters/FlowToScene';
	import {
		buildPersistableSceneDraft,
		fingerprintPersistableWorkspace,
	} from './helpers/workspaceFingerprint';
	import {
		validate_scene,
		check_for_broken_conditions_or_actions,
	} from '../Scenes/helpers/validators';
	import { postReq } from 'src/services/http';
	import { rooms } from 'src/stores';
	import FlowCanvas from './FlowCanvas/FlowCanvas.svelte';
	import FlowToolbar from './toolbar/FlowToolbar.svelte';
	import SceneJsonViewer from './FlowCanvas/SceneJsonViewer.svelte';
	import AddNodeModal from './modals/AddNodeModal.svelte';
	import FlowSettingsModal from './modals/FlowSettingsModal.svelte';
	import FlowTestResultsModal from './modals/FlowTestResultsModal.svelte';
	import FlowTemplatesModal from './modals/FlowTemplatesModal.svelte';
	import FlowOutlineModal from './modals/FlowOutlineModal.svelte';
	import NodeInspectorModal from './PropertyEditors/NodeInspectorModal.svelte';
	import { get } from 'svelte/store';
	import { canUndo, canRedo } from '../../stores/flow_editor';
	import { simulateFlowGraph, type FlowSimulationResult } from './helpers/flowTestSimulation';
	import { edit, initialEdit } from 'src/stores/ui/edit_lists';
	import { isFlow } from 'src/components/Lists/list-helpers';
	import { migrateIslandTriggerGatesInScene } from './converters/flow-island-utils';
	import { validateFlowWorkspace } from './helpers/nodeValidation';
	import FlowSharingModal from './modals/FlowSharingModal.svelte';
	import { createPortableFlowPackage, materializePortableFlow } from './sharing/portableFlow';
	import { encodeFlowToken } from './sharing/flowToken';
	import { takePendingFlowImport } from './sharing/importHandoff';
	import type { FlowImportMapping, PortableFlowPackage } from './sharing/types';
	import { deriveFlowEditorReadiness } from './helpers/flowEditorReadiness';
	import FlowRepairModal from './modals/FlowRepairModal.svelte';
	import {
		createReusableSubflow,
		instantiateReusableSubflow,
		loadReusableSubflows,
		saveReusableSubflow,
	} from './core/templates/reusable-subflows';
	import { user_data } from 'src/stores/account/user';
	import {
		getFlowTierLimits,
		getFlowUsageWithWorkspace,
		limitExceeded,
	} from './core/config/flow-entitlements';
	import { trackFlowUxEvent } from './core/ux/flow-ux-telemetry';

	function flowsBasePath(): string {
		return $location.startsWith('/automation/') ? '/automation/flows' : '/home/flows';
	}

	function flowsListPath(): string {
		return `${flowsBasePath()}/-1`;
	}

	function flowEditPath(sceneId: string): string {
		return `${flowsBasePath()}/${sceneId}/edit`;
	}

	function hydrateFlowSceneFromStore(scene: ISceneObj): ISceneObj {
		return migrateIslandTriggerGatesInScene(JSON.parse(JSON.stringify(scene)));
	}

	function sceneHasFlowGraph(s: ISceneObj | null | undefined): boolean {
		const fg = (s as any)?._meta?.flowGraph;
		if (!fg?.nodes) return false;
		const n = fg.nodes;
		if (Array.isArray(n)) return n.length > 0;
		if (typeof n === 'object') return Object.keys(n).length > 0;
		return false;
	}

	function workspaceValidationMessages(report: ReturnType<typeof validateFlowWorkspace>): string[] {
		const byId = new Map($flowEditorState.nodes.map((node) => [node.id, node]));
		return [
			...new Set(
				report.errors.map((issue) => {
					if (!issue.nodeId) return issue.message;
					const node = byId.get(issue.nodeId);
					const label = String(
						node?.data.displayLabel ||
							node?.data.label ||
							node?.data.device_name ||
							'Unnamed node',
					).trim();
					return `${label}: ${issue.message}`;
				}),
			),
		];
	}

	export let flowId: string = 'new';

	let currentScene: ISceneObj | null = null;
	let showSaveErrorModal = false;
	let validationErrors: string[] = [];
	let isMobile = false;
	let isSaving = false;
	let isRunning = false;
	let saveState: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
	let saveStateMessage = '';
	let loadStatusMessage = '';
	let loadWarnings: string[] = [];
	let loadProblematicNodes: { id: string; label: string; reason: string }[] = [];
	let showLoadWarningDetails = false;
	let dismissLoadWarnings = false;
	let flowCanvasRef:
		| {
				focusNode?: (nodeId: string) => void;
				beginNodePlacement?: (
					nodeType: string,
					paletteKey: string,
					initialData?: Record<string, unknown>,
				) => void;
				beginTemplatePlacement?: (templateId: string) => void;
				addContextualNode?: (
					nodeType: string,
					paletteKey: string,
					initialData?: Record<string, unknown>,
				) => boolean;
		  }
		| undefined;
	let showJsonViewer = false;
	let jsonViewerScene: ISceneObj | null = null;
	let showShortcutsModal = false;
	let showAddNodeModal = false;
	let addNodeInitialView: 'when' | 'if' | 'then' | 'all' | undefined = undefined;
	let canvasContextAdd = false;
	let addNodeEntryPoint: 'toolbar' | 'canvas' | 'connection' = 'toolbar';
	let showFlowSettingsModal = false;
	let flowSettingsInitialTab: 'general' | 'validation' | 'history' = 'general';
	let showClearCanvasModal = false;
	let isTestingFlow = false;
	let testResult: FlowSimulationResult | null = null;
	let testScopeLabel = 'Whole flow';
	let showTestResultsModal = false;
	let showTemplatesModal = false;
	let showOutlineModal = false;
	let showSharingModal = false;
	let sharingMode: 'export' | 'import' = 'export';
	let showRepairModal = false;

	/** Stable fingerprint of last loaded/saved persistable workspace (scene JSON subset). */
	let baselineWorkspaceFingerprint = '';

	/** False until canvas finishes loading and baseline fingerprint is captured (avoid false cancel prompts). */
	let baselineReady = false;

	let showUnsavedChangesModal = false;
	let pendingNavigationPath = flowsListPath();
	let pendingWorkspaceReset = false;

	/** Bumped on editor mount / flow switch so FlowCanvas remounts with a clean XYFlow instance. */
	let canvasMountKey = 0;

	function initializeBlankFlow() {
		currentScene = {
			_enabled: false,
			_run_on_ingest: false,
			_meta: {
				name: '',
				image: 'fa-diagram-project',
				backgroundColor: 'rgb(102, 96, 237)',
				integrations: false,
				room: -1,
				roomn: '',
				position: -1,
				flowEdited: true,
				isFlow: true,
				sceneType: 'flow',
			},
			if: { or: [] },
			do: [],
			else: [],
		};
		sceneMetadata.set({
			name: '',
			room: -1,
			roomName: '',
			image: 'fa-diagram-project',
			backgroundColor: 'rgb(102, 96, 237)',
			activeTimeIntervals: [],
		});
		baselineWorkspaceFingerprint = '';
		baselineReady = false;
		flowEditorState.reset();
		canvasMountKey += 1;
	}

	$: storeSceneForFlow =
		flowId && flowId !== 'new' ? ($scenes[flowId] as ISceneObj | undefined) : undefined;

	// Re-hydrate when API delivers flowGraph that the editor snapshot is missing (page refresh race).
	$: if (
		storeSceneForFlow &&
		sceneHasFlowGraph(storeSceneForFlow) &&
		!sceneHasFlowGraph(currentScene) &&
		!$flowEditorState.isDirty
	) {
		currentScene = hydrateFlowSceneFromStore(storeSceneForFlow);
		flowEditorState.reset();
		canvasMountKey += 1;
	}

	$: editorReadiness = deriveFlowEditorReadiness({
		flowId,
		storeScene: storeSceneForFlow,
		currentScene,
	});
	$: awaitingFlowGraph = editorReadiness.phase === 'awaiting_scene';

	$: flowName = $sceneMetadata.name || '';

	// Clear a "name required" save error automatically as soon as the user types a name
	$: if ($sceneMetadata.name && $sceneMetadata.name.trim() && saveState === 'error') {
		saveState = 'idle';
		saveStateMessage = '';
	}

	// Check if mobile

	$: {
		if (typeof window !== 'undefined') {
			isMobile = window.innerWidth < 640;
		}
	}

	// Reactive: reload scene when flowId changes (fixes flow switching showing wrong content)
	let prevFlowId: string | undefined;
	$: if (flowId !== prevFlowId) {
		prevFlowId = flowId;
		baselineReady = false;
		canvasMountKey += 1;
		if (flowId !== 'new') {
			const scene = $scenes[flowId];
			if (scene) {
				currentScene = hydrateFlowSceneFromStore(scene);
				sceneMetadata.set({
					name: scene._meta?.name ?? '',
					room: scene._meta?.room ?? -1,
					roomName: scene._meta?.roomn ?? '',
					image: scene._meta?.image || 'fa-diagram-project',
					backgroundColor: scene._meta?.backgroundColor || 'rgb(102, 96, 237)',
					activeTimeIntervals: scene._meta?.activeTimeIntervals ?? [],
				});
				flowEditorState.reset();
			} else if (flowId) {
				push(flowsListPath());
			}
		} else {
			initializeBlankFlow();
		}
	}

	onMount(() => {
		window.addEventListener('resize', handleResize);
		window.addEventListener('beforeunload', handleBeforeUnload);
		queueMicrotask(() => {
			const pendingImport = takePendingFlowImport();
			if (pendingImport) applyImportedFlow(pendingImport.flow, pendingImport.mapping);
		});
	});

	onDestroy(() => {
		if (typeof window !== 'undefined') {
			window.removeEventListener('resize', handleResize);
			window.removeEventListener('beforeunload', handleBeforeUnload);
		}
	});

	function refreshBaselineFromWorkspace() {
		if (!currentScene) {
			baselineWorkspaceFingerprint = '';
			baselineReady = false;
			return;
		}
		const st = get(flowEditorState);
		const draft = buildPersistableSceneDraft(currentScene, st.nodes, st.edges, get(sceneMetadata));
		baselineWorkspaceFingerprint = fingerprintPersistableWorkspace(draft);
		baselineReady = true;
	}

	function hasWorkspaceChangesVsBaseline(): boolean {
		if (!currentScene || !baselineReady) return false;
		const st = get(flowEditorState);
		const draft = buildPersistableSceneDraft(currentScene, st.nodes, st.edges, get(sceneMetadata));
		return fingerprintPersistableWorkspace(draft) !== baselineWorkspaceFingerprint;
	}

	function handleBeforeUnload(e: BeforeUnloadEvent) {
		if (!currentScene || !baselineReady) return;
		flushLiveFlowPositions();
		if (hasWorkspaceChangesVsBaseline()) {
			e.preventDefault();
			e.returnValue = '';
		}
	}

	function handleResize() {
		isMobile = window.innerWidth < 640;
	}

	function sanitizeHistoryEntries(history: unknown): SceneExecutionHistoryEntry[] {
		if (!Array.isArray(history)) return [];
		return history
			.filter(
				(entry) => entry && typeof entry === 'object' && typeof (entry as any).ts === 'number',
			)
			.map((entry) => {
				const item = entry as any;
				return {
					ts: Number(item.ts),
					status: item.status,
					nodeId: item.nodeId,
					branch: item.branch,
					message: item.message,
				} as SceneExecutionHistoryEntry;
			});
	}

	function mergeFlowExecutionHistory(scene: ISceneObj): void {
		const sceneId = scene._id?.toString();
		const latestStoreScene = sceneId ? ($scenes?.[sceneId] as ISceneObj | undefined) : undefined;
		const merged = [
			...sanitizeHistoryEntries(currentScene?._meta?.flowExecutionHistory),
			...sanitizeHistoryEntries(latestStoreScene?._meta?.flowExecutionHistory),
			...sanitizeHistoryEntries(scene?._meta?.flowExecutionHistory),
		];

		const dedupedMap = new Map<string, SceneExecutionHistoryEntry>();
		for (const entry of merged) {
			const key = [
				entry.ts,
				entry.status || '',
				entry.nodeId || '',
				entry.branch || '',
				entry.message || '',
			].join('|');
			dedupedMap.set(key, entry);
		}

		scene._meta.flowExecutionHistory = Array.from(dedupedMap.values())
			.sort((a, b) => a.ts - b.ts)
			.slice(-5);
	}

	function setSaveError(message: string) {
		saveState = 'error';
		saveStateMessage = message;
	}

	function focusProblematicNode(nodeId: string) {
		flowCanvasRef?.focusNode?.(nodeId);
	}

	function handleCanvasLoadStatus(event: CustomEvent) {
		const detail = event.detail || {};
		loadStatusMessage = String(detail.message || '');
		loadWarnings = Array.isArray(detail.warnings) ? detail.warnings : [];
		loadProblematicNodes = Array.isArray(detail.problematicNodes) ? detail.problematicNodes : [];
		showLoadWarningDetails = false;
		dismissLoadWarnings = false;

		// Baseline from persisted scene JSON shape (positions come from `_meta.flowGraph` via Scene→Flow).
		// Do not flush XYFlow → store on load: the canvas may still hold stale {0,0} coords and would
		// overwrite positions that sceneToGraph already merged from flowGraph.
		queueMicrotask(() => {
			if (detail.skipPositionFlush !== true) {
				flushLiveFlowPositions();
			}
			refreshBaselineFromWorkspace();
			flowEditorState.markClean();
		});
	}

	function extractEventValue(event: any): string {
		const detail = event?.detail;
		if (typeof detail === 'string' || typeof detail === 'number') return String(detail);
		if (detail && typeof detail === 'object' && 'value' in detail) return String(detail.value ?? '');
		const targetValue = event?.target?.value;
		if (typeof targetValue === 'string' || typeof targetValue === 'number') {
			return String(targetValue);
		}
		return '';
	}

	function handleFlowNameChange(event: any) {
		const value = extractEventValue(event);
		sceneMetadata.update((m) => ({ ...m, name: value }));
		if (saveState === 'error') {
			saveState = 'idle';
			saveStateMessage = '';
		}
	}

	function handleFlowRoomChange(event: any) {
		const raw = extractEventValue(event) || '-1';
		const value = Number(raw);
		const roomName = value === -1 ? '' : ($rooms as any)?.[value]?.name || '';
		sceneMetadata.update((m) => ({ ...m, room: value, roomName }));
		if (saveState === 'error') {
			saveState = 'idle';
			saveStateMessage = '';
		}
	}

	async function handleSave(): Promise<boolean> {
		if (isSaving) return false;
		isSaving = true;
		saveState = 'saving';
		saveStateMessage = 'Saving Flow…';
		try {
			let stateValue = $flowEditorState;
			// Apply any pending inspector changes before conversion (user may have edited
			// in the modal without clicking Save there)
			if (stateValue.pendingNode?.id) {
				flowEditorState.applyPendingNode();
				stateValue = $flowEditorState;
			}
			flushLiveFlowPositions();
			stateValue = $flowEditorState;
			const meta = $sceneMetadata;
			const savedFlows = (Object.values($scenes) as ISceneObj[]).filter((scene) => isFlow(scene));
			const tierLimits = getFlowTierLimits(Boolean($user_data?.premium));
			const projectedUsage = getFlowUsageWithWorkspace(
				savedFlows,
				flowId,
				stateValue.nodes,
				stateValue.edges,
			);
			if (limitExceeded(projectedUsage.flows, tierLimits.flows)) {
				validationErrors = [`Your plan allows ${tierLimits.flows} flows.`];
				showSaveErrorModal = true;
				setSaveError(validationErrors[0]);
				trackFlowUxEvent({
					name: 'save_validation_failed',
					surface: 'canvas',
					reason: 'flow-limit',
					issueCount: 1,
				});
				return false;
			}
			if (limitExceeded(projectedUsage.islands, tierLimits.islands)) {
				validationErrors = [
					`Your plan allows ${tierLimits.islands} execution islands across all flows.`,
				];
				showSaveErrorModal = true;
				setSaveError(validationErrors[0]);
				trackFlowUxEvent({
					name: 'save_validation_failed',
					surface: 'canvas',
					reason: 'island-limit',
					issueCount: 1,
				});
				return false;
			}

			if (!currentScene) {
				validationErrors = ['No Flow loaded'];
				showSaveErrorModal = true;
				setSaveError('No Flow loaded');
				trackFlowUxEvent({
					name: 'save_validation_failed',
					surface: 'canvas',
					reason: 'missing-scene',
					issueCount: 1,
				});
				return false;
			}

			// Rebuild scene from current graph + sidebar metadata (matches persisted Flow JSON).
			const updatedScene = buildPersistableSceneDraft(
				currentScene,
				stateValue.nodes,
				stateValue.edges,
				meta,
			);
			if (updatedScene._id === undefined || updatedScene._id === null) {
				updatedScene._enabled = false;
				updatedScene._run_on_ingest = false;
			}
			mergeFlowExecutionHistory(updatedScene);
			const workspaceValidation = validateFlowWorkspace(stateValue.nodes, stateValue.edges);
			if (!workspaceValidation.isValid) {
				validationErrors = workspaceValidationMessages(workspaceValidation);
				showSaveErrorModal = true;
				setSaveError(validationErrors[0] || 'Flow validation failed');
				trackFlowUxEvent({
					name: 'save_validation_failed',
					surface: 'canvas',
					reason: 'workspace',
					issueCount: validationErrors.length,
				});
				return false;
			}

			// Apply sidebar metadata (name, room, roomn) into scene before save
			if (updatedScene._meta) {
				updatedScene._meta.name = (meta.name || updatedScene._meta.name || '').trim();
				updatedScene._meta.room = meta.room ?? updatedScene._meta.room;
				updatedScene._meta.roomn = meta.roomName || updatedScene._meta.roomn || '';
			}

			// Run structural validation
			const errors = validateFlow(updatedScene);
			if (errors.length > 0) {
				validationErrors = errors;
				showSaveErrorModal = true;
				setSaveError(errors[0] || 'Flow validation failed');
				trackFlowUxEvent({
					name: 'save_validation_failed',
					surface: 'canvas',
					reason: 'scene-structure',
					issueCount: errors.length,
				});
				return false;
			}

			// Check for broken conditions/actions (returns true if broken)
			const hasBrokenItems = check_for_broken_conditions_or_actions(updatedScene);
			if (hasBrokenItems) {
				validationErrors = [
					$format('error_broken_conditions_or_actions', {
						default:
							'Some conditions or actions could not be interpreted. Please review your flow.',
					}),
				];
				showSaveErrorModal = true;
				setSaveError(validationErrors[0]);
				trackFlowUxEvent({
					name: 'save_validation_failed',
					surface: 'canvas',
					reason: 'uninterpretable',
					issueCount: 1,
				});
				return false;
			}

			// Prepare _meta.room / roomn like SceneDrawer (roomn from rooms store if needed)
			if (updatedScene._meta) {
				updatedScene._meta.room = updatedScene._meta.room === '' ? -1 : updatedScene._meta.room;
				if (typeof $rooms?.[updatedScene._meta.room] !== 'undefined') {
					updatedScene._meta.roomn =
						$rooms[updatedScene._meta.room].name ?? updatedScene._meta.roomn;
				}
				if (updatedScene._meta.room === -1) {
					updatedScene._meta.roomn = updatedScene._meta.roomn || 'General';
				}

				updatedScene._meta.sceneType = 'flow';
				updatedScene._meta.isFlow = true;

				if (
					!updatedScene._id ||
					Number($scenes[String(updatedScene._id)]?._meta?.room) !==
						Number(updatedScene._meta.room)
				) {
					updatedScene._meta.position =
						Object.values($scenes).filter(
							(sc) =>
								!(sc as any).tschem &&
								(sc as any)._gui_function !== 'alarm' &&
								isFlow(sc as ISceneObj) &&
								Number((sc as ISceneObj)._meta?.room) ===
									Number(updatedScene._meta?.room),
						).length || 0;
				}
			}

			let res: any = {};

			if (updatedScene._id === undefined || updatedScene._id === null) {
				res = await postReq('/scene/add', {
					scene_script: JSON.stringify(updatedScene),
				});
				if (res?.isok && res?.data?.scene_id) {
					updatedScene._id = res.data.scene_id;
					scenes.addScene(updatedScene);
					saveState = 'saved';
					saveStateMessage = 'Flow saved';
					currentScene = JSON.parse(JSON.stringify(updatedScene));
					baselineWorkspaceFingerprint = fingerprintPersistableWorkspace(updatedScene);
					baselineReady = true;
					flowEditorState.markClean();
					replace(flowEditPath(String(updatedScene._id)));
					return true;
				}
			} else {
				res = await postReq('/scene/edit', {
					id: updatedScene._id,
					scene_script: JSON.stringify(updatedScene),
				});
				// API often returns isok without data.scene_id on edit (see ControlAlarm, ThermoTempFavPositions).
				if (res?.isok) {
					if (res?.data?.scene_id != null) {
						updatedScene._id = String(res.data.scene_id);
					}
					scenes.editScene(updatedScene);
					saveState = 'saved';
					saveStateMessage = 'Flow updated';
					currentScene = JSON.parse(JSON.stringify(updatedScene));
					baselineWorkspaceFingerprint = fingerprintPersistableWorkspace(updatedScene);
					baselineReady = true;
					flowEditorState.markClean();
					return true;
				}
			}

			validationErrors = [
				res?.message || $format('error_save_failed', { default: 'Failed to save Flow' }),
			];
			showSaveErrorModal = true;
			setSaveError(validationErrors[0]);
			return false;
		} catch (error) {
			console.error('Failed to save flow:', error);
			validationErrors = ['Failed to save: ' + (error as Error).message];
			showSaveErrorModal = true;
			setSaveError(validationErrors[0]);
			return false;
		} finally {
			isSaving = false;
		}
	}

	function navigateExitConfirmed() {
		showUnsavedChangesModal = false;
		if ($edit) {
			initialEdit.set(true);
		}
		const targetPath = pendingNavigationPath;
		pendingNavigationPath = flowsListPath();
		if (pendingWorkspaceReset) {
			pendingWorkspaceReset = false;
			initializeBlankFlow();
		}
		push(targetPath);
	}

	export function requestNavigation(targetPath: string) {
		flushLiveFlowPositions();
		pendingWorkspaceReset = targetPath === flowsListPath() && flowId === 'new';
		if (hasWorkspaceChangesVsBaseline()) {
			pendingNavigationPath = targetPath;
			showUnsavedChangesModal = true;
			return;
		}
		pendingNavigationPath = targetPath;
		navigateExitConfirmed();
	}

	function handleCancel() {
		requestNavigation(flowsListPath());
	}

	function hasAnyCondition(obj: any): boolean {
		if (!obj || typeof obj !== 'object') return false;
		if (obj.or && Array.isArray(obj.or) && obj.or.length > 0) return true;
		if (obj.and && Array.isArray(obj.and) && obj.and.length > 0) return true;
		if (obj.if != null && (obj.do !== undefined || obj.else !== undefined)) {
			return (
				hasAnyCondition(obj.if) ||
				hasAnyDeep(obj.do, hasAnyCondition) ||
				hasAnyDeep(obj.else, hasAnyCondition)
			);
		}
		if (Array.isArray(obj)) return obj.some(hasAnyCondition);
		return false;
	}

	function hasAnyAction(obj: any): boolean {
		if (!obj || typeof obj !== 'object') return false;
		if (obj.r || obj.set !== undefined || obj.notify || obj.trigger || obj.grpctrl !== undefined)
			return true;
		if (obj.if != null && (obj.do !== undefined || obj.else !== undefined)) {
			return hasAnyDeep(obj.do, hasAnyAction) || hasAnyDeep(obj.else, hasAnyAction);
		}
		if (Array.isArray(obj.blk)) return obj.blk.some(hasAnyAction);
		if (Array.isArray(obj)) return obj.some(hasAnyAction);
		return false;
	}

	function hasAnyDeep(val: any, predicate: (v: any) => boolean): boolean {
		if (val == null) return false;
		if (Array.isArray(val)) return val.some(predicate);
		if (typeof val === 'object') return predicate(val);
		return false;
	}

	function validateFlow(sceneObj: ISceneObj): string[] {
		const errors: string[] = [];

		// Basic name validation
		if (!sceneObj._meta?.name || sceneObj._meta.name.trim() === '') {
			errors.push($format('error_flow_name_required', { default: 'Flow name is required' }));
		}

		// Run scene structural validator
		if (!validate_scene(sceneObj)) {
			// Check for conditions anywhere (top-level or nested if/do/else)
			if (
				!hasAnyCondition(sceneObj.if) &&
				!hasAnyCondition(sceneObj.do) &&
				!hasAnyCondition(sceneObj.else)
			) {
				errors.push(
					$format('error_no_conditions', { default: 'At least one condition is required' }),
				);
			}
			if (!hasAnyAction(sceneObj.do) && !hasAnyAction(sceneObj.else)) {
				errors.push($format('error_no_actions', { default: 'At least one action is required' }));
			}
			if (!sceneObj._meta?.image && !sceneObj._meta?.backgroundColor) {
				errors.push(
					$format('error_no_image', {
						default: 'Flow icon or background color is required',
					}),
				);
			}
		}

		return errors;
	}

	function handleAddNodeFromModal(
		nodeType: string,
		paletteKey: string,
		initialData?: Record<string, unknown>,
	) {
		showAddNodeModal = false;
		if (canvasContextAdd) {
			canvasContextAdd = false;
			if (flowCanvasRef?.addContextualNode?.(nodeType, paletteKey, initialData)) return;
		}
		flowCanvasRef?.beginNodePlacement?.(nodeType, paletteKey, initialData);
	}

	function openAddNode(initialView?: 'when' | 'if' | 'then' | 'all') {
		addNodeInitialView = initialView;
		canvasContextAdd = false;
		addNodeEntryPoint = 'toolbar';
		showAddNodeModal = true;
	}

	function handleToolbarAdd(selection: 'all' | 'when' | 'if' | 'do' | 'note' = 'all') {
		if (selection === 'note') {
			addNodeInitialView = undefined;
			canvasContextAdd = false;
			addNodeEntryPoint = 'toolbar';
			handleAddNodeFromModal('note', 'note');
			return;
		}
		if (selection === 'when') return openAddNode('when');
		if (selection === 'if') return openAddNode('if');
		if (selection === 'do') return openAddNode('then');
		openAddNode('all');
	}

	function handleCanvasRequestAdd(event: CustomEvent<{ entryPoint?: 'canvas' | 'connection' }>) {
		addNodeInitialView = undefined;
		canvasContextAdd = true;
		addNodeEntryPoint = event.detail?.entryPoint ?? 'canvas';
		showAddNodeModal = true;
	}

	function handleTemplateFromModal(templateId: string) {
		showAddNodeModal = false;
		showTemplatesModal = false;
		if (templateId.startsWith('reusable:')) {
			const subflow = loadReusableSubflows().find(
				(item) => item.id === templateId.slice('reusable:'.length),
			);
			if (!subflow) return;
			const instance = instantiateReusableSubflow(subflow, { x: 120, y: 120 });
			flowEditorState.setNodesAndEdges(
				[...$flowEditorState.nodes, ...instance.nodes],
				[...$flowEditorState.edges, ...instance.edges],
				{ markDirty: true },
			);
			flowEditorState.setSelectedNodeIds(instance.nodes.map((node) => node.id));
			return;
		}
		flowCanvasRef?.beginTemplatePlacement?.(templateId);
	}

	function saveSelectionAsSubflow() {
		const selectedIds = $flowEditorState.selectedNodeIds;
		if (!selectedIds.length) {
			loadStatusMessage = 'Select one or more nodes before saving a reusable subflow.';
			return;
		}
		const name = window.prompt('Name this reusable subflow');
		if (name === null) return;
		saveReusableSubflow(
			createReusableSubflow(name, $flowEditorState.nodes, $flowEditorState.edges, selectedIds),
		);
		loadStatusMessage = 'Reusable subflow saved in this browser.';
	}

	function handleClear() {
		showClearCanvasModal = true;
	}

	function confirmClearCanvas() {
		flowEditorState.reset();
		showClearCanvasModal = false;
	}

	function openFlowSettings(tab: 'general' | 'validation' | 'history' = 'general') {
		flowSettingsInitialTab = tab;
		showFlowSettingsModal = true;
	}

	async function handleFlowSettingsSave() {
		await handleSave();
		if (saveState !== 'error') showFlowSettingsModal = false;
	}

	function handleViewJson() {
		if (!currentScene) return;
		const stateValue = $flowEditorState;
		const m = get(sceneMetadata);
		const s = buildPersistableSceneDraft(currentScene, stateValue.nodes, stateValue.edges, m);
		jsonViewerScene = s;
		showJsonViewer = true;
	}

	function openFlowSharing(mode: 'export' | 'import') {
		sharingMode = mode;
		showSharingModal = true;
	}

	function buildFlowExport(includeNotes: boolean): {
		token: string;
		flow: PortableFlowPackage;
	} {
		flushLiveFlowPositions();
		const state = get(flowEditorState);
		const nodes = includeNotes ? state.nodes : state.nodes.filter((node) => node.type !== 'note');
		const nodeIds = new Set(nodes.map((node) => node.id));
		const edges = state.edges.filter(
			(edge) => nodeIds.has(String(edge.source)) && nodeIds.has(String(edge.target)),
		);
		const validation = validateFlowWorkspace(nodes, edges);
		if (!validation.isValid) {
			throw new Error(validation.errors[0]?.message || 'Fix Flow errors before exporting');
		}
		const flow = createPortableFlowPackage({
			nodes,
			edges,
			metadata: {
				name: $sceneMetadata.name || currentScene?._meta?.name || 'Shared Flow',
				image: $sceneMetadata.image || currentScene?._meta?.image,
				backgroundColor: $sceneMetadata.backgroundColor || currentScene?._meta?.backgroundColor,
			},
		});
		return { flow, token: encodeFlowToken(flow) };
	}

	function applyImportedFlow(flow: PortableFlowPackage, mapping: FlowImportMapping) {
		const imported = materializePortableFlow(flow, mapping);
		const importedScene = graphToScene(imported.nodes, imported.edges);
		currentScene = {
			...importedScene,
			_enabled: false,
			_run_on_ingest: false,
			_meta: {
				...importedScene._meta,
				name: `${flow.metadata.name || 'Imported Flow'} copy`,
				image: flow.metadata.image || 'fa-diagram-project',
				backgroundColor: flow.metadata.backgroundColor || 'rgb(102, 96, 237)',
				integrations: false,
				room: -1,
				roomn: '',
				position: -1,
				flowEdited: true,
				isFlow: true,
				sceneType: 'flow',
				flowGraph: {
					version: 1,
					nodes: imported.nodes,
					edges: imported.edges,
					savedAt: Date.now(),
				},
			},
		};
		sceneMetadata.set({
			name: currentScene._meta.name,
			room: -1,
			roomName: '',
			image: currentScene._meta.image || 'fa-diagram-project',
			backgroundColor: currentScene._meta.backgroundColor || 'rgb(102, 96, 237)',
			activeTimeIntervals: [],
		});
		flowEditorState.setNodesAndEdges(imported.nodes, imported.edges, { markDirty: true });
		canvasMountKey += 1;
		baselineReady = true;
		baselineWorkspaceFingerprint = '';
		loadWarnings = [
			...flow.warnings,
			...(imported.unresolvedRequirementIds.length
				? [
						`${imported.unresolvedRequirementIds.length} required resource(s) remain unresolved. Open the affected nodes before saving.`,
					]
				: []),
		];
		dismissLoadWarnings = false;
		showSharingModal = false;
	}

	function handleFlowImport(
		event: CustomEvent<{ flow: PortableFlowPackage; mapping: FlowImportMapping }>,
	) {
		applyImportedFlow(event.detail.flow, event.detail.mapping);
	}

	async function handleRun() {
		if (isRunning || isSaving) return;
		flushLiveFlowPositions();
		const workspaceValidation = validateFlowWorkspace(
			$flowEditorState.nodes,
			$flowEditorState.edges,
		);
		if (!workspaceValidation.isValid) {
			validationErrors = workspaceValidationMessages(workspaceValidation);
			showSaveErrorModal = true;
			setSaveError(validationErrors[0] || 'Flow validation failed');
			return;
		}
		if ($flowEditorState.isDirty || hasWorkspaceChangesVsBaseline()) {
			const saved = await handleSave();
			if (!saved) return;
		}
		const sceneId = currentScene?._id;
		if (!sceneId) return;
		const runStartedAt = Date.now();
		const runNodeCount = $flowEditorState.nodes.filter((node) => node.type !== 'note').length;
		isRunning = true;
		trackFlowUxEvent({
			name: 'run_started',
			surface: 'test',
			elapsedMs: 0,
			nodeCount: runNodeCount,
		});
		try {
			await postReq('scene/manual_run', { id: sceneId });
			trackFlowUxEvent({
				name: 'run_completed',
				surface: 'test',
				elapsedMs: Date.now() - runStartedAt,
				nodeCount: runNodeCount,
			});
		} catch (e) {
			console.error('Failed to run scene:', e);
			trackFlowUxEvent({
				name: 'run_failed',
				surface: 'test',
				elapsedMs: Date.now() - runStartedAt,
				nodeCount: runNodeCount,
			});
		} finally {
			setTimeout(() => {
				isRunning = false;
			}, 1500);
		}
	}

	function connectedIsland(
		nodeId: string,
		nodes: typeof $flowEditorState.nodes,
		edges: typeof $flowEditorState.edges,
	) {
		const adjacency = new Map(nodes.map((node) => [node.id, new Set<string>()]));
		for (const edge of edges) {
			adjacency.get(edge.source)?.add(edge.target);
			adjacency.get(edge.target)?.add(edge.source);
		}
		const ids = new Set<string>();
		const queue = [nodeId];
		while (queue.length) {
			const id = queue.shift()!;
			if (ids.has(id)) continue;
			ids.add(id);
			queue.push(...(adjacency.get(id) ?? []));
		}
		return {
			nodes: nodes.filter((node) => ids.has(node.id)),
			edges: edges.filter((edge) => ids.has(edge.source) && ids.has(edge.target)),
		};
	}

	async function handleTestFlow(scope: 'flow' | 'selection' | 'island' = 'flow') {
		if (isTestingFlow) return;
		isTestingFlow = true;
		flushLiveFlowPositions();
		flowEditorState.clearSimulationHighlight();
		try {
			const st = get(flowEditorState);
			const selectedId = st.selectedNodeId;
			let testNodes = st.nodes;
			let testEdges = st.edges;
			let entryNodeIds: string[] | undefined;
			if (scope === 'selection' && selectedId) {
				entryNodeIds = [selectedId];
				testScopeLabel = 'From selected card';
			} else if (scope === 'island' && selectedId) {
				const island = connectedIsland(selectedId, st.nodes, st.edges);
				testNodes = island.nodes;
				testEdges = island.edges;
				testScopeLabel = `Selected island · ${island.nodes.length} cards`;
			} else {
				testScopeLabel = 'Whole flow';
			}
			const result = await simulateFlowGraph(testNodes, testEdges, { entryNodeIds });
			testResult = result;
			showTestResultsModal = true;
			flowEditorState.setSimulationHighlight(result.nodeIds, result.edgeIds);
			const parts = [
				`Preview: ${result.nodeIds.length} node(s), ${result.edgeIds.length} edge(s) on the simulated path.`,
				...result.messages,
			];
			loadStatusMessage = parts.filter(Boolean).join(' ');
		} catch (e) {
			console.error('Flow preview failed:', e);
			loadStatusMessage = 'Flow preview failed — see console';
		} finally {
			isTestingFlow = false;
		}
	}

	$: currentWorkspaceValidation = validateFlowWorkspace(
		$flowEditorState.nodes,
		$flowEditorState.edges,
	);
	$: canRun = !!currentScene && currentWorkspaceValidation.isValid && !isSaving;

	function focusNodeFromModal(nodeId: string) {
		showFlowSettingsModal = false;
		showTestResultsModal = false;
		showOutlineModal = false;
		flowCanvasRef?.focusNode?.(nodeId);
	}
</script>

<Container padding="0" class="flow-editor-container" noScroll>
	<FlowToolbar
		flowName={flowName || currentScene?._meta?.name || ''}
		{saveState}
		{saveStateMessage}
		{loadStatusMessage}
		{isSaving}
		canUndo={$canUndo}
		canRedo={$canRedo}
		onCancel={handleCancel}
		onSave={handleSave}
		onAddNode={handleToolbarAdd}
		onClear={handleClear}
		onSettings={() => openFlowSettings('general')}
		onValidation={() => openFlowSettings('validation')}
		onHistory={() => openFlowSettings('history')}
		onShortcuts={() => (showShortcutsModal = true)}
		onUndo={() => flowEditorState.undo()}
		onRedo={() => flowEditorState.redo()}
		onLayout={() => flowEditorState.requestLayout()}
		onRun={handleRun}
		{isRunning}
		{canRun}
		onTestFlow={handleTestFlow}
		{isTestingFlow}
		onViewJson={handleViewJson}
		onTemplates={() => (showTemplatesModal = true)}
		onSaveSubflow={saveSelectionAsSubflow}
		onOutline={() => (showOutlineModal = true)}
		onExport={() => openFlowSharing('export')}
		onImport={() => openFlowSharing('import')}
	/>

	{#if loadWarnings.length > 0 && !dismissLoadWarnings}
		<div class="load-warnings">
			<i class="fa fa-triangle-exclamation"></i>
			<span>{loadWarnings[0]}</span>
			<div class="load-warning-actions">
				<button class="warning-btn" on:click={() => (showRepairModal = true)}>Repair</button>
				{#if loadWarnings.length > 1}
					<button
						class="warning-btn"
						on:click={() => (showLoadWarningDetails = !showLoadWarningDetails)}
					>
						{showLoadWarningDetails ? 'Hide details' : `Details (${loadWarnings.length})`}
					</button>
				{/if}
				<button class="warning-btn" on:click={() => (dismissLoadWarnings = true)}>Dismiss</button
				>
			</div>
		</div>
		{#if showLoadWarningDetails && loadWarnings.length > 1}
			<div class="load-warning-details">
				{#each loadWarnings as warning, index}
					<div>{index + 1}. {warning}</div>
				{/each}
			</div>
		{/if}
		{#if loadProblematicNodes.length > 0}
			<div class="load-warning-nodes">
				<span class="load-warning-nodes-label">Affected nodes:</span>
				{#each loadProblematicNodes as item (item.id)}
					<button
						type="button"
						class="problem-node-chip"
						title={item.reason}
						on:click={() => focusProblematicNode(item.id)}
					>
						{item.label}
					</button>
				{/each}
			</div>
		{/if}
	{/if}

	<div class="editor-container">
		<div class="canvas-wrapper">
			{#if awaitingFlowGraph}
				<div class="canvas-hydration-placeholder" role="status" aria-live="polite">
					<i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
					<span
						>{$format('label_loading_flow_layout', {
							default: 'Loading flow layout…',
						})}</span
					>
				</div>
			{:else}
				<div class="canvas-surface">
					{#key canvasMountKey}
						<FlowCanvas
							bind:this={flowCanvasRef}
							bind:scene={currentScene}
							{isMobile}
							on:loadstatus={handleCanvasLoadStatus}
							on:requestadd={handleCanvasRequestAdd}
						/>
					{/key}
				</div>
			{/if}
		</div>
	</div>
</Container>

<AddNodeModal
	showModal={showAddNodeModal}
	{isMobile}
	initialView={addNodeInitialView}
	entryPoint={addNodeEntryPoint}
	onClose={() => {
		showAddNodeModal = false;
		canvasContextAdd = false;
	}}
	onAdd={handleAddNodeFromModal}
/>

<FlowSettingsModal
	showModal={showFlowSettingsModal}
	{isMobile}
	scene={currentScene}
	initialTab={flowSettingsInitialTab}
	onClose={() => (showFlowSettingsModal = false)}
	onSave={handleFlowSettingsSave}
	onFocusNode={focusNodeFromModal}
/>

<FlowTestResultsModal
	showModal={showTestResultsModal}
	{isMobile}
	result={testResult}
	onClose={() => (showTestResultsModal = false)}
	onFocusNode={focusNodeFromModal}
	scopeLabel={testScopeLabel}
	validationErrors={currentWorkspaceValidation.errors.length}
	validationWarnings={currentWorkspaceValidation.warnings.length}
	isBusy={isTestingFlow || isRunning}
	canTestSelection={Boolean($flowEditorState.selectedNodeId)}
	onPreview={() => handleTestFlow('flow')}
	onTestSelection={() => handleTestFlow('selection')}
	onTestIsland={() => handleTestFlow('island')}
	onRun={handleRun}
/>

<FlowTemplatesModal
	showModal={showTemplatesModal}
	{isMobile}
	onClose={() => (showTemplatesModal = false)}
	onChoose={handleTemplateFromModal}
/>

<FlowOutlineModal
	showModal={showOutlineModal}
	nodes={$flowEditorState.nodes}
	edges={$flowEditorState.edges}
	onClose={() => (showOutlineModal = false)}
	onFocusNode={focusNodeFromModal}
/>

<FlowSharingModal
	showModal={showSharingModal}
	{isMobile}
	mode={sharingMode}
	buildExport={buildFlowExport}
	on:close={() => (showSharingModal = false)}
	on:import={handleFlowImport}
/>

<FlowRepairModal
	showModal={showRepairModal}
	{isMobile}
	onClose={() => (showRepairModal = false)}
	onFocusNode={focusNodeFromModal}
/>

<NodeInspectorModal {isMobile} />

{#if showJsonViewer}
	<SceneJsonViewer scene={jsonViewerScene} onClose={() => (showJsonViewer = false)} />
{/if}

<Modal
	showModal={showClearCanvasModal}
	on:close={() => (showClearCanvasModal = false)}
	title="Clear canvas?"
	backdropClose={false}
>
	<svelte:fragment slot="content">
		<Text variant="body" size="m">
			This removes every node and connection from the flow. You can still use Undo immediately
			afterward.
		</Text>
	</svelte:fragment>
	<div slot="footer" class="clear-canvas-actions">
		<Button variant="secondary" on:click={() => (showClearCanvasModal = false)}>Cancel</Button>
		<Button variant="danger" on:click={confirmClearCanvas}>Clear canvas</Button>
	</div>
</Modal>

<!-- Keyboard Shortcuts Modal -->
<Modal
	showModal={showShortcutsModal}
	on:close={() => (showShortcutsModal = false)}
	title="Keyboard Shortcuts"
	zIndex={1000}
>
	<svelte:fragment slot="content">
		<div class="shortcuts-grid">
			{#each [{ keys: ['Ctrl', 'Z'], desc: 'Undo' }, { keys: ['Ctrl', 'Y'], desc: 'Redo' }, { keys: ['Ctrl', 'C'], desc: 'Copy selection' }, { keys: ['Ctrl', 'X'], desc: 'Cut selection' }, { keys: ['Ctrl', 'V'], desc: 'Paste duplicate of clipboard' }, { keys: ['Del / Backspace'], desc: 'Delete selection' }, { keys: ['Ctrl / ⌘', 'Click'], desc: 'Add/remove node from selection' }, { keys: ['Drag'], desc: 'Marquee on empty canvas; drag a node to move selection' }, { keys: ['Click'], desc: 'Select node / open properties' }, { keys: ['Middle / right', 'Drag'], desc: 'Pan canvas' }, { keys: ['Space', 'Drag'], desc: 'Pan canvas (Space held)' }, { keys: ['Scroll'], desc: 'Zoom in / out' }, { keys: ['?'], desc: 'Show this dialog' }] as shortcut}
				<div class="shortcut-row">
					<div class="shortcut-keys">
						{#each shortcut.keys as key}
							<kbd>{key}</kbd>
						{/each}
					</div>
					<span class="shortcut-desc">{shortcut.desc}</span>
				</div>
			{/each}
		</div>
	</svelte:fragment>
	<div slot="footer">
		<Button variant="primary" on:click={() => (showShortcutsModal = false)}>Close</Button>
	</div>
</Modal>

<!-- Save Error Modal -->
<Modal
	showModal={showSaveErrorModal}
	on:close={() => (showSaveErrorModal = false)}
	title={$format('label_validation_errors', { default: 'Validation Errors' })}
	zIndex={1000}
>
	<svelte:fragment slot="content">
		<Stack gap="s">
			<Text variant="body" size="m"
				>{$format('label_fix_errors_before_saving', {
					default: 'Please fix the following errors before saving:',
				})}</Text
			>
			{#each validationErrors as error}
				<Text variant="body" size="s" class="error-text">• {error}</Text>
			{/each}
		</Stack>
	</svelte:fragment>
	<div slot="footer">
		<Button variant="primary" on:click={() => (showSaveErrorModal = false)}>
			{$format('label_ok', { default: 'OK' })}
		</Button>
	</div>
</Modal>

<!-- Unsaved changes when leaving editor -->
<Modal
	showModal={showUnsavedChangesModal}
	on:close={() => (showUnsavedChangesModal = false)}
	title={$format('flow_unsaved_title', { default: 'Unsaved changes' })}
	zIndex={1000}
>
	<svelte:fragment slot="content">
		<Text variant="body" size="m">
			{$format('flow_unsaved_body', {
				default: 'Your flow has changes that are not saved yet. Leave without saving?',
			})}
		</Text>
	</svelte:fragment>
	<div slot="footer">
		<Stack gap="s" direction="row">
			<Button variant="secondary" on:click={() => (showUnsavedChangesModal = false)}>
				{$format('flow_unsaved_stay', { default: 'Keep editing' })}
			</Button>
			<Button variant="danger" on:click={navigateExitConfirmed}>
				{$format('flow_unsaved_leave', { default: 'Leave without saving' })}
			</Button>
		</Stack>
	</div>
</Modal>

<style>
	:global(.flow-editor-container) {
		position: relative;
		width: 100%;
		background: var(--color-base, #151617);
		flex: 1;
		min-height: 0;
		height: 100%;
		overflow: hidden;
		display: flex;
		flex-direction: column;
	}

	.clear-canvas-actions {
		display: flex;
		justify-content: flex-end;
		gap: 12px;
	}

	:global(.flow-editor-container) > :global(.main-slot-container) {
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
	}

	:global(.flow-editor-container) :global(.flow-editor-toolbar) {
		flex-shrink: 0;
		position: relative;
		z-index: 100;
	}

	/* ── Load warnings ───────────────────────────────────────── */
	.load-warnings {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 0.75rem;
		background: rgba(245, 158, 11, 0.12);
		border-top: 1px solid rgba(245, 158, 11, 0.3);
		border-bottom: 1px solid rgba(245, 158, 11, 0.3);
		color: #fde68a;
		font-size: 0.8rem;
		flex-shrink: 0;
	}

	.load-warning-actions {
		margin-left: auto;
		display: inline-flex;
		gap: 0.4rem;
	}

	.warning-btn {
		background: transparent;
		border: 1px solid rgba(245, 158, 11, 0.45);
		color: #fde68a;
		border-radius: 6px;
		padding: 2px 8px;
		font-size: 0.75rem;
		cursor: pointer;
	}

	.warning-btn:hover {
		background: rgba(245, 158, 11, 0.16);
	}

	.load-warning-details {
		padding: 0.45rem 0.75rem 0.65rem;
		background: rgba(245, 158, 11, 0.08);
		border-bottom: 1px solid rgba(245, 158, 11, 0.22);
		color: #fef3c7;
		font-size: 0.78rem;
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
		flex-shrink: 0;
	}

	.load-warning-nodes {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 0.35rem;
		padding: 0.45rem 0.75rem 0.6rem;
		background: rgba(239, 68, 68, 0.08);
		border-bottom: 1px solid rgba(239, 68, 68, 0.22);
		flex-shrink: 0;
	}

	.load-warning-nodes-label {
		font-size: 0.75rem;
		color: #fecaca;
		margin-right: 0.15rem;
	}

	.problem-node-chip {
		background: rgba(239, 68, 68, 0.18);
		border: 1px solid rgba(239, 68, 68, 0.45);
		color: #fee2e2;
		border-radius: 999px;
		padding: 2px 10px;
		font-size: 0.72rem;
		cursor: pointer;
		max-width: 14rem;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.problem-node-chip:hover {
		background: rgba(239, 68, 68, 0.28);
	}

	/* ── Editor layout ───────────────────────────────────────── */
	.editor-container {
		flex: 1;
		display: flex;
		overflow: hidden;
		position: relative;
		min-height: 0;
	}

	.canvas-hydration-placeholder {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 12px;
		min-height: 200px;
		color: #9ca3af;
		font-size: 0.9rem;
	}

	.canvas-hydration-placeholder i {
		font-size: 1.25rem;
		color: #6b7280;
	}

	.canvas-wrapper {
		flex: 1;
		min-width: 0;
		min-height: 0;
		position: relative;
		overflow: hidden;
		/* flex-column so the SvelteFlow canvas fills via flex:1, avoiding height:100% chain */
		display: flex;
		flex-direction: column;
	}

	/* Override SvelteFlow's height:100% with flex:1 so it fills canvas-wrapper reliably */
	.canvas-wrapper :global(.flow-canvas) {
		flex: 1 !important;
		height: auto !important;
		min-height: 0;
	}

	.canvas-surface {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
	}

	/* ── Keyboard shortcuts modal ───────────────────────────────*/
	.shortcuts-grid {
		display: flex;
		flex-direction: column;
		gap: 6px;
		padding: 4px 0;
	}

	.shortcut-row {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 5px 8px;
		border-radius: 6px;
	}

	.shortcut-row:hover {
		background: rgba(255, 255, 255, 0.04);
	}

	.shortcut-keys {
		display: flex;
		gap: 4px;
		min-width: 160px;
		flex-shrink: 0;
	}

	kbd {
		display: inline-flex;
		align-items: center;
		padding: 2px 7px;
		background: #2a2a2a;
		border: 1px solid #444;
		border-bottom-width: 2px;
		border-radius: 5px;
		font-family: monospace;
		font-size: 0.72rem;
		color: #e5e7eb;
		white-space: nowrap;
	}

	.shortcut-desc {
		font-size: 0.82rem;
		color: #9ca3af;
	}

	/* ── Misc ────────────────────────────────────────────────── */
	:global(.error-text) {
		color: #ef4444;
	}

	@media (max-width: 640px) {
		.editor-container {
			flex-direction: column;
		}
	}
</style>
